import _extends from "@babel/runtime/helpers/extends";
var _DomainActionsErrorsM, _DomainActionsErrorsM2, _DomainActionsErrorsM3;
import { DomainProductTypeEnum } from '@wix/ambassador-premium-domains-v1-recommendation/types';
import { Box } from '@wix/design-system';
import { CoreDomainSearchStore, DomainActionsErrorsMap, DomainSearchSuiteModule } from '@wix/domains-search-suite';
import { DomainsApiClient } from '@wix/domains-serverless-interface/react-kit';
import { usePremiumEssentialsContext } from '@wix/premium-fed-sdk/@premium-essentials';
import clsx from 'clsx';
import React, { useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { GeneralAnonymousOverride, SubdomainRootTaken } from '../../../Components';
import { Base44Label, base44MainCardBadgeClassNames } from '../../../Components/Base44Label';
import { Base44ResultsHeader } from '../../../Components/Headers';
import { BackButton } from '../../../Components/Headers/HeaderWrapper';
import { Base44VoucherBanner } from '../../../Components/VoucherBanner/Base44VoucherBanner';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { ExperimentSpecs, SEARCH_QUERY_PARAMS } from '../../../constants';
import { ResultsLayout, ResultsRecommendations, ResultsSearchBar } from '../../ResultsLayout';
import s from '../../anonymousResultsPage.scss';
const InlineMobileActions = _ref => {
  let pricing = _ref.pricing,
    getItButton = _ref.getItButton;
  return /*#__PURE__*/React.createElement(Box, {
    direction: "horizontal",
    align: "space-between",
    verticalAlign: "middle",
    width: "100%"
  }, pricing, getItButton);
};
const customConfiguration = {
  ...(((_DomainActionsErrorsM = DomainActionsErrorsMap.assign) == null ? void 0 : _DomainActionsErrorsM.reduce((acc, status) => ({
    ...acc,
    [status]: GeneralAnonymousOverride
  }), {})) ?? {}),
  ...(((_DomainActionsErrorsM2 = DomainActionsErrorsMap.connect) == null ? void 0 : _DomainActionsErrorsM2.reduce((acc, status) => ({
    ...acc,
    [status]: GeneralAnonymousOverride
  }), {})) ?? {}),
  ...(((_DomainActionsErrorsM3 = DomainActionsErrorsMap.transfer) == null ? void 0 : _DomainActionsErrorsM3.reduce((acc, status) => ({
    ...acc,
    [status]: GeneralAnonymousOverride
  }), {})) ?? {}),
  transfer: GeneralAnonymousOverride,
  connect: GeneralAnonymousOverride,
  subdomainRootIsNotConnected: SubdomainRootTaken,
  domainTakenInWix: GeneralAnonymousOverride,
  subdomainTakenInWix: SubdomainRootTaken,
  noStatus: GeneralAnonymousOverride
};
export const EmbeddedResultsPage = _ref2 => {
  let handlers = _ref2.handlers,
    externalViewer = _ref2.externalViewer,
    deviceType = _ref2.deviceType,
    onSearchSubmit = _ref2.onSearchSubmit;
  return /*#__PURE__*/React.createElement(DomainsApiClient.Provider, {
    overrides: {
      getPurchasePermissions: {
        hasPermission: true
      }
    }
  }, /*#__PURE__*/React.createElement(DomainSearchSuiteModule.Provider, {
    translations: {
      Loading: /*#__PURE__*/React.createElement(React.Fragment, null)
    },
    actions: {
      siteInfo: {
        externalViewer: Boolean(externalViewer)
      },
      handlers
    },
    statusesCustomComponents: {
      customConfiguration
    }
  }, /*#__PURE__*/React.createElement(EmbeddedResultsPageContent, {
    deviceType: deviceType,
    onSearchSubmit: onSearchSubmit
  })));
};
export const EmbeddedResultsPageContent = _ref3 => {
  let deviceType = _ref3.deviceType,
    onSearchSubmit = _ref3.onSearchSubmit;
  const isMobile = useIsMobile();
  const history = useHistory();
  const _useLocation = useLocation(),
    search = _useLocation.search;
  const handleBackClick = useCallback(() => {
    DomainSearchSuiteModule.internals.bi.onCTAClick == null || DomainSearchSuiteModule.internals.bi.onCTAClick({
      ctaClick: 'back_button'
    });
    const params = new URLSearchParams(search);
    SEARCH_QUERY_PARAMS.forEach(param => params.delete(param));
    const nextSearch = params.toString();
    history.push(nextSearch ? `/?${nextSearch}` : '/');
    setTimeout(() => {
      CoreDomainSearchStore.resetStore();
    });
  }, [history, search]);
  const _usePremiumEssentials = usePremiumEssentialsContext(),
    experiments = _usePremiumEssentials.experiments;
  const isOptimized = Boolean(experiments == null ? void 0 : experiments[ExperimentSpecs.Base44ResultsOptimizations]);
  const layoutProps = isOptimized ? {
    hideSearchBarShadowOnPartialMatch: true,
    showSearchIcon: true,
    hideFilterChips: true,
    useCompactRecommendationsLayout: true,
    stickyHeaderOffset: '0',
    mobileVoucherInSearchBar: true,
    header: isMobile ? undefined : /*#__PURE__*/React.createElement(Base44ResultsHeader, {
      onBackClick: handleBackClick,
      hideTrustBuilders: true
    })
  } : {
    header: /*#__PURE__*/React.createElement(Base44ResultsHeader, {
      onBackClick: isMobile ? undefined : handleBackClick
    }),
    mobileBackButton: /*#__PURE__*/React.createElement(Box, {
      width: "100%"
    }, /*#__PURE__*/React.createElement(BackButton, {
      onClick: handleBackClick
    }))
  };
  const recommendationsProps = isOptimized ? {
    className: clsx(s.centeredContent, s.narrowContent),
    titleOverflowStrategy: 'scale',
    rowTitleOverflowStrategy: isMobile ? 'wrap' : undefined,
    clickableRows: isMobile,
    stackedMobileCards: true,
    Layouts: {
      Label: Base44Label,
      MobileActions: InlineMobileActions
    },
    mainCardBadgeClassNames: base44MainCardBadgeClassNames
  } : {
    className: s.centeredContent,
    isLeanView: true,
    hideAlternativesHeading: true,
    Layouts: {
      Label: Base44Label,
      MobileActions: InlineMobileActions
    }
  };
  return /*#__PURE__*/React.createElement(ResultsLayout, _extends({
    biViewMode: "Base44",
    productType: DomainProductTypeEnum.BASE44,
    querySubmitPath: "/results",
    deviceType: deviceType,
    headerPadding: isMobile ? '0 SP4' : '16px SP6',
    headerTop: 0,
    searchBarFullWidth: true,
    searchBar: /*#__PURE__*/React.createElement(ResultsSearchBar, {
      onSearchSubmit: onSearchSubmit,
      compactLayouts: isOptimized && isMobile
    }),
    voucher: /*#__PURE__*/React.createElement(Base44VoucherBanner, null)
  }, layoutProps), /*#__PURE__*/React.createElement(ResultsRecommendations, _extends({
    showHeroCard: true,
    hideAiInsights: true,
    hideFreeWithSitePlanBadge: true,
    pricingOptimized: false,
    width: "100%",
    padding: isMobile ? '0 SP4' : '0 SP6'
  }, recommendationsProps)));
};
//# sourceMappingURL=EmbeddedResultsPage.js.map